import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Col, DatePicker, Divider, message, Row, Space, Spin, Statistic, Tag } from "antd";
import dayjs from "dayjs";
import {
  useGetSavedReportByIdQuery,
  useRunAnalyticsQueryMutation,
  useExportAnalyticsReportMutation,
  useGetAnalyticsDatasetsQuery,
} from "../../Redux/service/Analytics/Analytics";
import TableView from "../Analytics/Visualizations/TableView";
import trans from "../../utils";
import { ClientReportExportContext } from "../../context/ClientReportExportContext";

const { RangePicker } = DatePicker;

function normalizeDate(value) {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

function formatNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  if (Number.isInteger(value)) return value;
  return parseFloat(value.toFixed(2));
}

function formatData(data) {
  if (Array.isArray(data)) {
    return data.map(formatData);
  }
  if (data && typeof data === "object") {
    const formatted = {};
    for (const key of Object.keys(data)) {
      formatted[key] = formatData(data[key]);
    }
    return formatted;
  }
  return formatNumber(data);
}

export default function ClientReportRunner() {
  const { reportId } = useParams();
  const { registerHandlers } = useContext(ClientReportExportContext);
  const { data: reportRes, isLoading: isLoadingReport, error: reportError } = useGetSavedReportByIdQuery(reportId);
  const { data: datasetsRes } = useGetAnalyticsDatasetsQuery();
  const [runQuery, { data: resultData, isLoading: isRunning, error: runError }] = useRunAnalyticsQueryMutation();
  const [exportReport, { isLoading: isExporting }] = useExportAnalyticsReportMutation();

  const [dateRange, setDateRange] = useState([null, null]);

  const report = reportRes?.data;
  const datasets = datasetsRes?.data || [];

  const datasetName = useMemo(() => {
    if (!report?.query?.datasetId) return "-";
    return datasets.find((d) => d.id === report.query.datasetId)?.name || "-";
  }, [report, datasets]);

  const result = resultData?.data || { columns: [], rows: [] };

  const formattedResult = useMemo(() => ({
    columns: result.columns,
    rows: formatData(result.rows),
    cards: result.cards ? formatData(result.cards) : result.cards,
  }), [result]);

  useEffect(() => {
    if (report?.query?.dateRange) {
      const from = normalizeDate(report.query.dateRange.from);
      const to = normalizeDate(report.query.dateRange.to);
      setDateRange([from, to]);
      handleRun(report.query, from, to);
    }
  }, [report]);

  function buildQuery(baseQuery, from, to) {
    return {
      ...baseQuery,
      dateRange:
        from && to
          ? { from: from.toISOString(), to: to.toISOString() }
          : undefined,
    };
  }

  async function handleRun(baseQuery = report?.query, from = dateRange[0], to = dateRange[1]) {
    if (!baseQuery) return;
    if (!from || !to) {
      message.warning(trans("Please select a date range"));
      return;
    }
    try {
      await runQuery(buildQuery(baseQuery, from, to)).unwrap();
    } catch (err) {
      message.error(err?.data?.message || trans("Failed to run report"));
    }
  }

  async function doExport(format) {
    if (!report?.query) return;
    if (!dateRange[0] || !dateRange[1]) {
      message.warning(trans("Please select a date range"));
      return;
    }
    try {
      const query = buildQuery(report.query, dateRange[0], dateRange[1]);
      const blob = await exportReport({ query, format, title: report.name }).unwrap();
      const extension = format === "pdf" ? "pdf" : "xlsx";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.name.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err?.message || trans("Failed to export"));
    }
  }

  useEffect(() => {
    registerHandlers({
      exportPdf: () => doExport("pdf"),
      exportExcel: () => doExport("excel"),
      loading: isExporting,
    });
    return () => registerHandlers(null);
  }, [registerHandlers, report, dateRange, isExporting]);

  if (isLoadingReport) {
    return (
      <div className="p-4">
        <Spin />
      </div>
    );
  }

  if (reportError || !report) {
    return (
      <div className="p-4">
        <Alert
          message={reportError?.data?.message || trans("Report not found")}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="report-view p-4">
      <Card title={report.name} style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <strong>{trans("Dataset")}:</strong> {datasetName}
            </div>
            {report.query?.filters?.length > 0 && (
              <div>
                <strong>{trans("Filters")}:</strong>{" "}
                {report.query.filters.map((f, i) => (
                  <Tag key={i}>
                    {f.fieldId} {f.op} {Array.isArray(f.value) ? f.value.join(", ") : f.value}
                  </Tag>
                ))}
              </div>
            )}
            <Space>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                showTime={false}
                format="YYYY-MM-DD"
              />
              <Button type="primary" onClick={() => handleRun()} loading={isRunning}>
                {trans("Run Report")}
              </Button>
            </Space>
          </Space>
        </Card>

        {runError && (
          <Alert
            message={runError?.data?.message || runError?.message || trans("Failed to run report")}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {formattedResult.cards?.length > 0 && (
          <>
            <Divider orientation="left">{trans("Dashboard Cards")}</Divider>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {formattedResult.cards.map((card) => (
                <Col key={card.key} xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic title={card.label} value={card.value ?? "-"} />
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        <Spin spinning={isRunning}>
          <TableView columns={formattedResult.columns} rows={formattedResult.rows} />
        </Spin>
      </div>
  );
}
